import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { Box } from "@mui/system";
import { GridFilterAltIcon, GridSearchIcon } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import dayjs, { Dayjs } from "dayjs";
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import translate from "../../../i18n/translate";
import useOutsideAlerter from "../../../modules/hooks/useOutsideAlterer";
import CustomDateRangePicker from "../CustomDateRangePicker";
import FilterItem from "../FilterItem";

type Props = {
  onlyPaidField?: boolean,
  sortByNameField?: boolean
  setFilterRequestString: (arg: string | undefined) => void
  fetchRequest: (filters: string | undefined) => void
  searchPlaceholderId?: string
}


const Filter = ({setFilterRequestString, fetchRequest: executeQuery, onlyPaidField, sortByNameField, searchPlaceholderId}: Props) => {
  const intl = useIntl();
  const searchLabel = intl.formatMessage({ id: searchPlaceholderId || 'filterSearch' });
  const filterLabel = intl.formatMessage({ id: 'filterTitle' });
  const [isSortShown, setIsSortShown] = useState(false);
  const [isSearchShown, setIsSearchShown] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const defaultFilterState = {
    onlyPaid: false,
    sortByName: false,
    ascending: false,
    dateFrom: null,
    dateTo: null,
    searchField: "",
  }

  const wrappedRef = useRef(null);
  const searchWrapperRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const skipNextSearchClickRef = useRef(false);
  useOutsideAlerter(wrappedRef, setIsSortShown);

  const [filters, setFilters] = useState<any>(defaultFilterState)

  const hasSearchText = Boolean((filters.searchField || '').trim())

  useEffect(() => {
    if (isSearchShown) {
      requestAnimationFrame(() => searchInputRef.current?.focus())
    }
  }, [isSearchShown])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const node = searchWrapperRef.current
      if (node && !node.contains(event.target as Node) && !hasSearchText) {
        setIsSearchShown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [hasSearchText])

  const onSearchFieldChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFilters({...filters, searchField: event.target.value})
  }
  const onRestoreFilterClick = () => {
    setFilters(defaultFilterState)
    setFilterRequestString(undefined)
    executeQuery(undefined)
  }

  const changeFilterCheckBoxValue = (key: any) => {
    setFilters({...filters, [key]: !filters[key]});
  }

  const onSearchClear = () => {
    const nextFilters = { ...filters, searchField: "" }
    setFilters(nextFilters)

    let reqStr = `&${nextFilters.sortByName ? "sortByName" : "sortByDate"}=${nextFilters.ascending ? "ASC" : "DESC"}`

    if (nextFilters.onlyPaid) reqStr += `&onlyPaid=true`

    if (nextFilters.dateFrom && dayjs(nextFilters.dateFrom, 'YYYY-MM-DD', true).isValid())
      reqStr += `&dateFrom=${nextFilters.dateFrom?.format('YYYY-MM-DD')}`

    if (nextFilters.dateTo && dayjs(nextFilters.dateTo, 'YYYY-MM-DD', true).isValid())
      reqStr += `&dateTo=${nextFilters.dateTo?.format('YYYY-MM-DD')}`

    executeQuery(reqStr)
    setFilterRequestString(reqStr)
    searchInputRef.current?.focus()
  }
  const changeFilterDateStartValue = (value: Dayjs | null) => {
    setFilters({...filters, dateFrom: value});
  }
  const changeFilterDateEndValue = (value: Dayjs) => {
    setFilters({...filters, dateTo: value});
  }

  const onSearchClick = () => {
    let reqStr = `&${filters.sortByName ? "sortByName" : "sortByDate"}=${filters.ascending ? "ASC" : "DESC"}`


    if(filters.onlyPaid)
      reqStr += `&onlyPaid=true`

    if(filters.dateFrom && dayjs(filters.dateFrom, 'YYYY-MM-DD', true).isValid())
      reqStr+=`&dateFrom=${filters.dateFrom?.format('YYYY-MM-DD')}`

    if(filters.dateTo && dayjs(filters.dateTo, 'YYYY-MM-DD', true).isValid())
      reqStr+=`&dateTo=${filters.dateTo?.format('YYYY-MM-DD')}`


    if(filters.searchField && filters.searchField !== "")
      reqStr+=`&search=${filters.searchField}`

    executeQuery(reqStr)
    setFilterRequestString(reqStr)
    setIsSearchShown(false)
  }
  const onFilterClick = async () => {
    let reqStr = `&${filters.sortByName ? "sortByName" : "sortByDate"}=${filters.ascending ? "ASC" : "DESC"}`


    if(filters.onlyPaid)
      reqStr += `&onlyPaid=true`

    if(filters.dateFrom && dayjs(filters.dateFrom, 'YYYY-MM-DD', true).isValid())
      reqStr+=`&dateFrom=${filters.dateFrom?.format('YYYY-MM-DD')}`

    if(filters.dateTo && dayjs(filters.dateTo, 'YYYY-MM-DD', true).isValid())
      reqStr+=`&dateTo=${filters.dateTo?.format('YYYY-MM-DD')}`

    executeQuery(reqStr)
    setFilterRequestString(reqStr)
  }

  const onSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && hasSearchText) {
      event.preventDefault()
      onSearchClick()
      searchInputRef.current?.blur()
    }
    if (event.key === 'Escape') {
      setIsSearchShown(false)
      searchInputRef.current?.blur()
    }
  }

  return (
      <div style={{
          width: "100%",
          height: "50px",
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          paddingRight: "10px",
          gap: "5px"
        }}>
          <div style={{position: "relative"}}>
            <IconButton
              onClick={()=> setIsSortShown(true)}
              aria-label={filterLabel}
              sx={{
                width: 40,
                height: 40,
                padding: 0,
                color: "orange",
              }}>
                <GridFilterAltIcon sx={{ fontSize: 24 }}/>
            </IconButton>
            { isSortShown && (
              <Box
              ref={wrappedRef}
              sx={{
                padding: "10px 10px 10px 10px",
                backgroundColor: "white",
                border: "1px solid orange",
                position: "absolute",
                top: 50,
                right: 0,
                zIndex: 300,
                minWidth: "260px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
              }}>
                <CustomDateRangePicker
                  dateFrom={filters.dateFrom}
                  setDateFrom={changeFilterDateStartValue}
                  dateTo={filters.dateTo}
                  setDateTo={changeFilterDateEndValue}
                />
                {onlyPaidField && (
                  <FilterItem
                  value={filters.onlyPaid}
                  setValue={changeFilterCheckBoxValue}
                  code="onlyPaid"
                  label={translate('filterItemOnlyPaid')} />
                )}
                {sortByNameField && (
                  <FilterItem
                  value={filters.sortByName}
                  setValue={changeFilterCheckBoxValue}
                  code="sortByName"
                  label={translate('filterItemSortByName')} />
                )}
                <FilterItem
                value={filters.ascending}
                setValue={changeFilterCheckBoxValue}
                code="ascending"
                label={translate('filterItemAscending')} />

                <Button fullWidth color="warning" onClick={onFilterClick}>
                  {translate('filterSubmit')}
                </Button>
                <Button fullWidth color="warning" onClick={onRestoreFilterClick}>
                  {translate('filterRestore')}
                </Button>
              </Box>
            )}
          </div>

          <Box ref={searchWrapperRef} sx={{ display: "flex", alignItems: "center", position: "relative", zIndex: 200, backgroundColor: "#fff" }}>
            {isSearchShown || hasSearchText ? (
              <TextField
                inputRef={searchInputRef}
                size="small"
                variant="outlined"
                color="warning"
                value={filters.searchField}
                onChange={onSearchFieldChange}
                onKeyDown={onSearchKeyDown}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder={searchLabel}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {hasSearchText && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={onSearchClear}
                          aria-label={intl.formatMessage({ id: 'filterRestore' })}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="warning"
                        onMouseDown={(e) => {
                          if (hasSearchText && !isSearchFocused) {
                            e.preventDefault()
                            searchInputRef.current?.focus()
                            skipNextSearchClickRef.current = true
                          }
                        }}
                        onClick={() => {
                          if (skipNextSearchClickRef.current) {
                            skipNextSearchClickRef.current = false
                            return
                          }
                          onSearchClick()
                        }}
                        aria-label={searchLabel}
                      >
                        <GridSearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: hasSearchText && !isSearchFocused
                    ? `${Math.min(320, Math.max(120, filters.searchField.length * 9 + 80))}px`
                    : 320,
                  transition: 'width 0.2s ease',
                  '.MuiOutlinedInput-root': {
                    height: 40,
                    paddingRight: 0,
                  },
                }}
              />
            ) : (
              <IconButton
                onClick={() => setIsSearchShown(true)}
                color="warning"
                aria-label={searchLabel}
                sx={{
                  width: 40,
                  height: 40,
                  padding: 0,
                }}
              >
                <GridSearchIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
          </Box>
      </div>
  )
}

export default Filter;
